import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, PencilSimple, Trash, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import NumberInput from "@/components/ui/NumberInput";

const empty = { numero: "", descripcion: "", activo: true, es_default: false };

export default function PuntosEmisionTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rec/puntos-emision");
      setItems(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, numero: String(it.numero) }); setOpen(true); };

  const save = async () => {
    try {
      const payload = {
        numero: Number(form.numero),
        descripcion: form.descripcion || null,
        activo: !!form.activo,
        es_default: !!form.es_default,
      };
      if (!payload.numero || payload.numero <= 0) {
        toast.error("El número debe ser mayor a 0");
        return;
      }
      if (editing) {
        await api.put(`/rec/puntos-emision/${editing.id}`, payload);
        toast.success("Punto de emisión actualizado");
      } else {
        await api.post("/rec/puntos-emision", payload);
        toast.success("Punto de emisión creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const del = async (it) => {
    if (!confirm(`¿Eliminar punto de emisión ${it.numero}?`)) return;
    try {
      await api.delete(`/rec/puntos-emision/${it.id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Puntos de emisión REC</CardTitle>
        <Button size="sm" onClick={openNew} data-testid="new-pe-btn">
          <Plus size={16} className="mr-1" /> Nuevo
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center text-neutral-500 py-8">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-neutral-400 py-12">
            Sin puntos de emisión — se creará uno automáticamente al usar el módulo.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-neutral-500 border-b">
                <tr>
                  <th className="text-left py-2 w-24">Número</th>
                  <th className="text-left">Descripción</th>
                  <th className="text-left w-24">Activo</th>
                  <th className="text-left w-32">Default</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b hover:bg-neutral-50" data-testid={`pe-row-${it.numero}`}>
                    <td className="py-2 font-mono font-bold">{String(it.numero).padStart(4, "0")}</td>
                    <td>{it.descripcion || <span className="text-neutral-400">-</span>}</td>
                    <td>
                      {it.activo
                        ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Activo</Badge>
                        : <Badge variant="secondary">Inactivo</Badge>}
                    </td>
                    <td>
                      {it.es_default && (
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200">
                          <Star size={12} weight="fill" className="mr-1" /> Default
                        </Badge>
                      )}
                    </td>
                    <td className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(it)}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(it)}>
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar" : "Nuevo"} punto de emisión</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Número *</Label>
              <Input type="number"
                min="1"
                data-testid="pe-numero-input"
                value={form.numero}
                onChange={(e) => setForm({ ...form, numero: e.target.value })}
                placeholder="4"
              />
              <p className="text-xs text-neutral-500 mt-1">
                Debe estar habilitado previamente en AFIP para el servicio WSRemCarne.
              </p>
            </div>
            <div>
              <Label>Descripción</Label>
              <Input
                value={form.descripcion || ""}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                placeholder="Sucursal, mostrador, etc."
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.activo}
                onCheckedChange={(v) => setForm({ ...form, activo: v })}
                data-testid="pe-activo-switch"
              />
              <Label>Activo</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.es_default}
                onCheckedChange={(v) => setForm({ ...form, es_default: v })}
                data-testid="pe-default-switch"
              />
              <Label>Usar como default en el diálogo de emisión</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-pe-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
